/**
 * The session, as one small state machine.
 *
 *   loading ── tokens in the Keychain? ──▶ ready       (contexts resolved)
 *      │                                └▶ onboarding  (signed in, nothing to open)
 *      └────────────── no tokens ────────▶ anonymous
 *
 * `status` is DERIVED from what the server said, never set by a screen. That is what
 * makes the navigator honest: onboarding shows up exactly when the account owns no
 * company and works nowhere, and it disappears the moment that stops being true —
 * there is no flag anyone can forget to flip.
 *
 * `logout()` is local-only and never touches the network: it is also the callback the
 * HTTP client fires when a refresh is rejected, and a logout that itself makes a
 * request could bounce off its own 401. `signOut()` is the user-initiated one — it
 * revokes the refresh token server-side first, best effort, then calls logout().
 */
import {Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {create} from 'zustand';

import {ApiError, forgetTokens, loadTokens, setTokens} from '@/shared/api';
import {SESSION_IN_COOKIES} from '@/shared/api/session-mode';
import {setContourCookie, clearContourCookie} from '@/shared/lib/contour';
import {clearPushRegistration, syncPushRegistration} from '@/shared/push';
import {
  fetchProfile,
  login as loginRequest,
  oauthExchange,
  confirmRegistration,
  register as registerRequest,
  resendRegistration,
  revokeSession,
  type Account,
  type OAuthOutcome,
  type PendingSignup,
  type RegisterInput,
} from '@/entities/session/api';
import {GOOGLE_PROVIDER} from '@/shared/config/oauth';
import {googleCredential} from '@/shared/lib/google-signin';
import {fetchContexts, type AppContext} from '@/entities/session/contexts';

export type SessionStatus =
  | 'loading'
  | 'anonymous'
  | 'must-change-password'
  | 'onboarding'
  | 'ready';

/**
 * Регистрация, ждущая кода из письма. Только в памяти: пароль нужен экрану кода
 * (сервер сверяет его при подтверждении), и ни в адресную строку, ни на диск он
 * попасть не должен — поэтому не параметр навигации и не хранилище.
 */
export type SignupInProgress = PendingSignup & {password: string; sentAt: number};

/** Вход упёрся в неподтверждённую почту: код уже отправлен, экран кода ждёт. */
export class EmailNotVerifiedError extends Error {
  constructor() {
    super('email_not_verified');
    this.name = 'EmailNotVerifiedError';
  }
}

function notVerifiedEmail(e: unknown): string | null | undefined {
  if (!(e instanceof ApiError) || e.status !== 403) return undefined;
  const details = (e.body as {details?: {error?: string; email?: string}} | null)?.details;
  if (details?.error !== 'email_not_verified') return undefined;
  return details.email ?? null;
}

export type AuthState = {
  status: SessionStatus;
  signup: SignupInProgress | null;
  account: Account | null;
  contexts: AppContext[];
  activeContext: AppContext | null;

  hydrate: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<void>;
  /**
   * Sign in with Google. Resolves to the outcome the gateway decided, or null when
   * the user dismissed the provider's dialog — which is a decision, not a failure,
   * and must not surface as an error.
   */
  loginWithGoogle: () => Promise<OAuthOutcome | null>;
  /** Форма регистрации отправлена — код ушёл на почту, дальше экран кода. */
  register: (input: RegisterInput) => Promise<void>;
  /** Код из письма → сессия. */
  confirmSignup: (code: string) => Promise<void>;
  resendSignup: () => Promise<void>;
  cancelSignup: () => void;
  /** Local sign-out. Safe to call from the HTTP client's 401 handler. */
  logout: () => Promise<void>;
  /** User-initiated sign-out: revoke on the server, then drop locally. */
  signOut: () => Promise<void>;
  /** Re-ask the server what this account can open (after creating a company, etc.). */
  reloadContexts: () => Promise<void>;
  setActiveContext: (context: AppContext) => void;
  /**
   * The forced password change went through. Called ONLY by the screen that owns it,
   * and only after the server answered 200 — that answer is the server clearing the
   * flag, which is why this is not the client deciding it no longer applies.
   */
  passwordChanged: () => Promise<void>;
  /** The HTTP client saw `must_change_password` on a token refresh. */
  requirePasswordChange: () => void;
};

/** Signed in with nowhere to go = onboarding. Not an error, and not an empty Home. */
function statusFor(contexts: AppContext[]): SessionStatus {
  return contexts.length > 0 ? 'ready' : 'onboarding';
}

/**
 * The password gate outranks everything the contexts say.
 *
 * A reset password arrives by e-mail in plain text and the tokens issued beside it
 * work everywhere, so the only thing standing between that mail and the account is
 * this branch. It comes FIRST — before onboarding, before ready — because "where can
 * this account go" is not a question worth answering while the answer is "nowhere
 * until the password is replaced".
 */
function statusUnderGate(
  contexts: AppContext[],
  mustChangePassword: boolean,
): SessionStatus {
  return mustChangePassword ? 'must-change-password' : statusFor(contexts);
}

/** What identifies a context across reloads — `kind` and company, nothing else. */
type ContextRef = Pick<AppContext, 'kind' | 'companyId'>;

const sameContext = (a: ContextRef, b: ContextRef) =>
  a.kind === b.kind && a.companyId === b.companyId;

/** Keep the user where they were if that context survived the reload; else first. */
function pickActive(
  contexts: AppContext[],
  previous: ContextRef | null,
): AppContext | null {
  if (previous) {
    const kept = contexts.find(c => sameContext(c, previous));
    if (kept) {
      return kept;
    }
  }
  return contexts[0] ?? null;
}

/**
 * Какой контекст открыть в следующий раз.
 *
 * `activeContext` живёт в памяти, а в вебе F5 — обычное дело: без записи каждая
 * перезагрузка отбрасывала владельца двух компаний в первую по списку, и «переключил,
 * обновил — опять не та» читалось как сломанный переключатель. Хранится только
 * {@link ContextRef}, по которому {@link pickActive} уже умеет найти контекст в свежем
 * списке: исчезнувший контекст даёт первый по списку, как и раньше. Ключ — по
 * аккаунту, чтобы на общем компьютере не унаследовать чужой выбор.
 *
 * Неудачная запись стоит выбора на СЛЕДУЮЩЕМ запуске, не на этом.
 */
const ACTIVE_CONTEXT_KEY = 'samvision.active-context';

type StoredContext = ContextRef & {accountId: string};

function rememberActive(accountId: string, context: ContextRef): void {
  const stored: StoredContext = {
    accountId,
    kind: context.kind,
    companyId: context.companyId,
  };
  void AsyncStorage.setItem(ACTIVE_CONTEXT_KEY, JSON.stringify(stored)).catch(() => {});
}

async function recallActive(accountId: string): Promise<ContextRef | null> {
  try {
    const raw = await AsyncStorage.getItem(ACTIVE_CONTEXT_KEY);
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw) as Partial<StoredContext>;
    if (stored.accountId !== accountId || !stored.kind || !stored.companyId) {
      return null;
    }
    return {kind: stored.kind, companyId: stored.companyId};
  } catch {
    // Unreadable storage means the first context, as before. Nothing to report.
    return null;
  }
}

const SIGNED_OUT = {
  status: 'anonymous' as const,
  account: null,
  contexts: [] as AppContext[],
  activeContext: null,
};

/**
 * How this device names itself in the account's token list. Free text with no
 * semantics — its only job is to let a person looking at "my devices" recognise which
 * row is the phone in their hand.
 */
function deviceLabel(): string {
  return `SamVision · ${Platform.OS}`;
}

/**
 * Attach this device to the signed-in account for push, without ever making that the
 * session's problem.
 *
 * Not awaited by any caller and it never throws: a phone that cannot reach the push
 * provider — or a build with no provider at all, which is today's state — must still
 * sign in normally. The whole thing is a no-op until `shared/push/transport.ts` is
 * wired up, and it is called here rather than from a screen because the obligation
 * belongs to the session, not to whichever screen happens to be mounted.
 */
function attachPush(): void {
  void syncPushRegistration(deviceLabel());
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  status: 'loading',
  signup: null,
  account: null,
  contexts: [],
  activeContext: null,

  hydrate: async () => {
    // Native: the stored pair is the cheap local check that spares a network
    // round-trip for someone who has never signed in on this device.
    //
    // Cookie mode has no such check to make — the credential is an HttpOnly
    // cookie this code cannot read, so "is there a session?" is a question only
    // the server can answer, and that is precisely what /auth/me below asks. A
    // signed-out visitor costs one 401 instead of nothing; a signed-in one gets
    // their session back across a page reload, which a page-local store could
    // never do.
    if (!SESSION_IN_COOKIES) {
      const tokens = await loadTokens();
      if (!tokens) {
        set(SIGNED_OUT);
        return;
      }
    }

    try {
      // /auth/me is the cheapest way to learn WHO the stored token belongs to, and
      // it doubles as the token's liveness check: a stale access token is refreshed
      // transparently by the client, and a dead refresh token throws.
      const account = await fetchProfile();
      // Single-domain: метим контур кукой sv_contour (из /auth/me) ДО
      // fetchContexts — по ней nginx отдаёт данные нужного контура (тот же
      // домен, без редиректа). Платформенного админа не трогаем — он ходит в
      // оба (контур пишет тумблером). На нативе setContourCookie — no-op.
      if (!account.isPlatformAdmin) {
        setContourCookie(account.contour);
      }
      const contexts = await fetchContexts(account.id);
      set({
        // `/auth/me` does not carry `must_change_password`, so a cold start cannot
        // learn about the gate here — the token refresh does, and either it already
        // fired during these two calls (keep what it decided) or it will within the
        // access token's hour. Recomputing blindly would undo it.
        status: statusUnderGate(contexts, get().status === 'must-change-password'),
        account,
        contexts,
        activeContext: pickActive(
          contexts,
          get().activeContext ?? (await recallActive(account.id)),
        ),
      });
      // Cold start with a live session: the provider may have rotated this device's
      // token while the app was closed, and nothing else would notice.
      attachPush();
    } catch (e) {
      if (e instanceof ApiError) {
        // The server refused us. The stored credential is worthless — drop it.
        await forgetTokens();
      }
      // A network failure is NOT a reason to burn the Keychain: the token may well
      // be valid and the phone merely be in a lift. We show the login screen (there
      // is nothing else to show), but the credential survives for the next launch.
      set(SIGNED_OUT);
    }
  },

  login: async (identifier, password) => {
    let session;
    try {
      session = await loginRequest(identifier, password);
    } catch (e) {
      // Регистрацию бросили до кода: сервер говорит это только при верном пароле.
      // Шлём новый код и уводим на экран кода — пароль у нас уже есть.
      const email = notVerifiedEmail(e);
      if (email === undefined) throw e;
      const target = email ?? identifier;
      const pending = await resendRegistration(target);
      set({signup: {...pending, email: target, password, sentAt: Date.now()}});
      throw new EmailNotVerifiedError();
    }
    await setTokens(session.tokens);
    // Контур несёт только /auth/me (login-ответ — нет), и поставить куку надо
    // ДО fetchContexts: без sv_contour данные ушли бы в release-гейтвей, и
    // бета-токен получил бы 401. Single-domain: метим кукой, не редиректим.
    // Платформенного админа не трогаем; на нативе — no-op.
    const account = await fetchProfile();
    if (!account.isPlatformAdmin) {
      setContourCookie(account.contour);
    }
    const contexts = await fetchContexts(account.id);
    set({
      status: statusUnderGate(contexts, session.mustChangePassword),
      account,
      contexts,
      activeContext: pickActive(contexts, await recallActive(session.account.id)),
    });
    attachPush();
  },

  loginWithGoogle: async () => {
    const credential = await googleCredential();
    if (!credential) {
      return null;
    }
    const session = await oauthExchange(GOOGLE_PROVIDER, credential);
    await setTokens(session.tokens);
    // The four reachable outcomes differ in what happened on the SERVER — a new
    // account, a link made by matching a verified email, or a sign-in through a link
    // that already existed — and in none of them does the client do anything
    // different: it has a token pair and an account, and what that account can open
    // is a question only fetchContexts answers. A `registered` account owns nothing,
    // so this lands on onboarding by the same rule as a fresh sign-up, without a
    // branch that could disagree with it.
    //
    // Контур — из /auth/me (login/oauth-ответ его не несёт) и ДО fetchContexts:
    // ставим куку sv_contour, чтобы данные ушли в свой гейтвей (иначе 401 на
    // бета-токене). Single-domain: метим кукой, не редиректим; натив — no-op.
    const account = await fetchProfile();
    if (!account.isPlatformAdmin) {
      setContourCookie(account.contour);
    }
    const contexts = await fetchContexts(account.id);
    set({
      status: statusUnderGate(contexts, session.mustChangePassword),
      account,
      contexts,
      activeContext: pickActive(contexts, await recallActive(session.account.id)),
    });
    attachPush();
    return session.outcome;
  },

  register: async input => {
    const pending = await registerRequest(input);
    set({signup: {...pending, password: input.password, sentAt: Date.now()}});
  },

  confirmSignup: async code => {
    const signup = get().signup;
    if (!signup) throw new Error('no_signup');
    const session = await confirmRegistration(signup.email, code, signup.password);
    await setTokens(session.tokens);
    // A brand-new account owns nothing, so this resolves to [] and the navigator
    // lands on onboarding. We ask anyway rather than assume: registration may one
    // day arrive by invite, already attached to an employer.
    //
    // Контур — из /auth/me и ДО fetchContexts (иначе 401 на чужом гейтвее).
    // Новый аккаунт по умолчанию release; single-domain метит кукой sv_contour.
    const account = await fetchProfile();
    if (!account.isPlatformAdmin) {
      setContourCookie(account.contour);
    }
    const contexts = await fetchContexts(account.id);
    set({
      signup: null,
      status: statusUnderGate(contexts, session.mustChangePassword),
      account,
      contexts,
      activeContext: pickActive(contexts, await recallActive(session.account.id)),
    });
    attachPush();
  },

  resendSignup: async () => {
    const signup = get().signup;
    if (!signup) return;
    const pending = await resendRegistration(signup.email);
    set({signup: {...signup, ...pending, email: signup.email, sentAt: Date.now()}});
  },

  cancelSignup: () => set({signup: null}),

  logout: async () => {
    await forgetTokens();
    // Снять контур-куку: следующий вход на этом браузере стартует как гость
    // (release), а не в контуре предыдущего пользователя. На нативе — no-op.
    clearContourCookie();
    set(SIGNED_OUT);
  },

  signOut: async () => {
    // FIRST, while the session still exists: DELETE /auth/push-token/{id} needs it,
    // and after forgetTokens() there is nothing left to authenticate the call with.
    // Skipping this is how the next person to sign in on this phone starts receiving
    // the previous employee's arrival notifications.
    await clearPushRegistration();

    const tokens = await loadTokens();
    if (tokens) {
      try {
        await revokeSession(tokens.refreshToken);
      } catch {
        // Offline, or the token was already dead. Either way the local session goes:
        // refusing to log out because the network is down would trap the user.
      }
    }
    await get().logout();
  },

  reloadContexts: async () => {
    const account = get().account;
    if (!account) {
      return;
    }
    const contexts = await fetchContexts(account.id);
    set({
      // Recomputing from contexts alone would LOWER the password gate: this call is
      // about which companies exist, and it must not answer a question it was not
      // asked. Same reason `hydrate` keeps it — /auth/me does not carry the flag.
      status: statusUnderGate(contexts, get().status === 'must-change-password'),
      contexts,
      activeContext: pickActive(contexts, get().activeContext),
    });
  },

  setActiveContext: context => {
    set({activeContext: context});
    const account = get().account;
    if (account) {
      rememberActive(account.id, context);
    }
  },

  requirePasswordChange: () => {
    // Only from a signed-in state: the flag rides on a token refresh, and a refresh
    // that happened after the session was dropped has nothing left to gate.
    if (get().status === 'ready' || get().status === 'onboarding') {
      set({status: 'must-change-password'});
    }
  },

  passwordChanged: async () => {
    if (get().status !== 'must-change-password') {
      return;
    }
    const account = get().account;
    // The contexts were resolved before the gate went up, but a session that came in
    // through `requirePasswordChange` may have been idle for a long time — ask again
    // rather than trust a list that is now of unknown age.
    const contexts = account ? await fetchContexts(account.id) : get().contexts;
    set({
      status: statusFor(contexts),
      contexts,
      activeContext: pickActive(contexts, get().activeContext),
    });
  },
}));
